"use client";

import { faTimes, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePathname, useSearchParams } from "next/navigation";
import { Fragment } from "react";
import type { Aggregation, ProductsQuery } from "~/app/[lang]/(default)/category/[id]/ProductsGrid";
import { useFilterOption } from "~/app/[lang]/(default)/category/[id]/ProductsGrid/Filter/useFilterOption";
import { Link } from "~/components/Link";

type ActiveFiltersProps = {
	aggregations: ProductsQuery["aggregations"];
	deleteFilterMessage: string;
};

export function ActiveFilters({ aggregations, deleteFilterMessage }: ActiveFiltersProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const mode = searchParams.get("mode");
	const deleteFilterHref = { pathname, query: mode ? { mode: mode } : null };

	const activeFilters = aggregations.filter((aggregation) =>
		searchParams?.has(aggregation.attribute_code),
	);

	if (activeFilters.length === 0) {
		return null;
	}

	return (
		<div className="container">
			<div className="scrollbar-none flex snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto overflow-y-hidden">
				{!!deleteFilterHref && (
					<Link
						href={deleteFilterHref}
						className="group relative flex snap-start items-center space-x-2 whitespace-nowrap rounded border border-primary bg-primary-light px-3 py-1.5 leading-none hover:border-primary-dark focus:outline-none focus-visible:border-primary-dark motion-safe:transition"
					>
						<span>{deleteFilterMessage}</span>
						<FontAwesomeIcon
							icon={faTrash}
							className="text-primary-medium text-sm group-hover:text-primary-dark group-focus-visible:text-primary-dark motion-safe:transition"
						/>
					</Link>
				)}

				{activeFilters.map((aggregation) => {
					return (
						<Fragment key={aggregation.attribute_code}>
							{aggregation.options.map((option) => (
								<ActiveFilter key={option.value} aggregation={aggregation} option={option} />
							))}
						</Fragment>
					);
				})}
			</div>
		</div>
	);
}

interface ActiveFilterProps {
	aggregation: Aggregation;
	option: Aggregation["options"][number];
}

function ActiveFilter({ aggregation, option }: ActiveFilterProps) {
	const { isActive, href } = useFilterOption({
		code: aggregation.attribute_code,
		value: option.value,
	});

	if (!isActive) {
		return null;
	}

	return (
		<Link
			href={href}
			className="group relative flex snap-start items-start space-x-2 whitespace-nowrap rounded border border-shades bg-shades-light px-3 py-1.5 leading-none hover:border-shades-dark focus:outline-none focus-visible:border-shades-dark motion-safe:transition"
		>
			<span>{option.label}</span>
			<FontAwesomeIcon
				icon={faTimes}
				className=" text-shades-medium group-hover:text-shades-dark group-focus-visible:text-shades-dark motion-safe:transition"
			/>
		</Link>
	);
}
